import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  useTheme,
} from "@mui/material";
import {
  ContentCopy as SimilarIcon,
  MergeType as MergeIcon,
} from "@mui/icons-material";
import { tokens } from "../../../../theme";
import type { SimilarVenue } from "../../types/venues.types";

interface SimilarVenuesCardProps {
  similarVenues: SimilarVenue[];
  loading: boolean;
  onVenueClick: (venueId: number) => void;
  onMergeClick: (secondaryVenueId: number) => void;
  canMerge: boolean;
  InfoCard: React.ComponentType<{
    title: string;
    icon: React.ReactNode;
    sx?: object;
    children: React.ReactNode;
  }>;
}

const getSimilarityColor = (score: number): "error" | "warning" | "success" => {
  if (score >= 70) return "success";
  if (score >= 50) return "warning";
  return "error";
};

export const SimilarVenuesCard: React.FC<SimilarVenuesCardProps> = ({
  similarVenues,
  loading,
  onVenueClick,
  onMergeClick,
  canMerge,
  InfoCard,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <InfoCard
      title="Similar Venues"
      sx={{ height: "100%" }}
      icon={<SimilarIcon />}
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : similarVenues.length === 0 ? (
        <Typography
          variant="h4"
          sx={{
            fontStyle: "italic",
            textAlign: "center",
            py: 4,
            fontFamily: '"Freehand", cursive',
          }}
        >
          No Similar Venues Found
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.primary[800] }}>
                <TableCell>Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>City</TableCell>
                <TableCell align="center">Similarity</TableCell>
                <TableCell align="center">Completeness</TableCell>
                {canMerge && <TableCell align="center">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {similarVenues.map((venue, index) => (
                <TableRow
                  key={venue.id}
                  sx={{
                    cursor: "pointer",
                    backgroundColor:
                      index % 2 === 0 ? "white" : colors.primary[800],
                    "&:hover": {
                      backgroundColor: colors.grey[500],
                    },
                  }}
                >
                  <TableCell onClick={() => onVenueClick(venue.id)}>
                    <Typography variant="body2" fontWeight="medium">
                      {venue.name}
                    </Typography>
                  </TableCell>
                  <TableCell onClick={() => onVenueClick(venue.id)}>
                    {venue.address_one || "-"}
                  </TableCell>
                  <TableCell onClick={() => onVenueClick(venue.id)}>
                    {venue.city || "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => onVenueClick(venue.id)}
                  >
                    <Chip
                      label={`${venue.similarity_score}%`}
                      color={getSimilarityColor(venue.similarity_score)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => onVenueClick(venue.id)}
                  >
                    <Chip
                      label={`${venue.completeness_score}%`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  {canMerge && (
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MergeIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMergeClick(venue.id);
                        }}
                      >
                        Merge
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </InfoCard>
  );
};

export default SimilarVenuesCard;
